import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PatientIdBadge from "../components/PatientIdBadge";
import TestLinksSection from "../components/TestLinksSection";
import { useAuth } from "../auth/AuthContext";
import {
  addPatientSharedNote,
  getPatient,
  getPatientScans,
  Patient,
  PatientSharedNote,
  ScanListItem,
} from "../api/patients";
import { formatLocalDateTime } from "../utils/datetime";
import {
  cognimeterDisplayPosition,
  cognimeterInterpretation,
} from "../components/Cognimeter";

// Physician: production-страница пациента (English UI): инфо, заметки, тесты.
// Без исследовательских полей (MoCA/Qmci/Depression/калибровки/Parts).

const SEX_LABELS: Record<string, string> = { male: "Male", female: "Female" };
const EDUCATION_LABELS: Record<string, string> = {
  incompleteSecondaryEducation: "Incomplete secondary",
  secondaryEducation: "Secondary",
  incompleteHigherEducation: "Incomplete higher",
  higherEducation: "Higher",
  academicDegree: "Academic degree",
};
const ROLE_LABELS: Record<string, string> = {
  physician: "physician",
  test_administrator: "nurse",
};

function operatorLabel(user: ScanListItem["user"]): string {
  if (!user) return "—";
  const name = [user.name, user.surname].filter(Boolean).join(" ");
  const role = user.role ? ROLE_LABELS[user.role] : null;
  const base = name || user.email;
  return role ? `${base} (${role})` : base;
}

function mciChip(scan: ScanListItem) {
  if (scan.mci_status === "done" && scan.mci_result?.confidence_pct != null) {
    const result = cognimeterInterpretation(
      cognimeterDisplayPosition(scan.mci_result.confidence_pct, scan.mci_result.thresholds)
    );
    const chip = result.category === "expected"
      ? { label: "Expected range", color: "success" as const }
      : result.category === "borderline"
        ? { label: "Borderline", color: "warning" as const }
        : { label: "Cognitive impairment", color: "error" as const };
    return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />;
  }
  if (scan.mci_status === "processing") {
    return <Chip label="Analyzing..." size="small" color="info" variant="outlined" />;
  }
  return null;
}

export default function PhysicianPatientPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // медсёстры (test_administrator) результатов не видят
  const canSeeResults = !!user && (user.role === "physician" || user.role === "admin" || !!user.is_superuser);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [scans, setScans] = useState<ScanListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      const [p, s] = await Promise.all([
        getPatient(patientId),
        getPatientScans(patientId, { limit: 100 }),
      ]);
      setPatient(p);
      setScans(s.items);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Patient not found");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    return <Alert severity="error">{error || "Patient not found"}</Alert>;
  }

  const unifiedScans = scans.filter((s) => s.scan_part === "unified");

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
          <PatientIdBadge patientId={patient.patient_id} />
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
            {patient.sex && <Chip label={SEX_LABELS[patient.sex] || patient.sex} size="small" variant="outlined" />}
            {patient.age != null && <Chip label={`${patient.age} y.o.`} size="small" variant="outlined" />}
            {patient.education && (
              <Chip label={EDUCATION_LABELS[patient.education] || patient.education} size="small" variant="outlined" />
            )}
            {patient.language && <Chip label={patient.language.toUpperCase()} size="small" variant="outlined" />}
          </Stack>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Created: {formatLocalDateTime(patient.created_at)}
          {patient.created_by_user?.email ? ` · ${patient.created_by_user.email}` : ""}
        </Typography>
        {patient.free_notes && (
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            {patient.free_notes}
          </Typography>
        )}
      </Paper>

      <TestLinksSection patientId={patient.patient_id} onStartNow={(token) => navigate(`/test/${token}`)} />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Tests ({unifiedScans.length})
        </Typography>
        {unifiedScans.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tests yet
          </Typography>
        ) : (
          <List disablePadding>
            {unifiedScans.map((scan, idx) => {
              const text = (
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {formatLocalDateTime(scan.created_at)}
                      </Typography>
                      {scan.display_mode === "web" && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Web · ${scan.administration === "supervised" ? "Supervised" : "Remote"}`}
                        />
                      )}
                      {scan.attempt_status === "interrupted" && (
                        <Chip size="small" color="warning" variant="outlined" label="Interrupted" />
                      )}
                      {scan.attempt_status === "in_progress" && (
                        <Chip size="small" color="info" variant="outlined" label="In progress" />
                      )}
                      {canSeeResults && mciChip(scan)}
                    </Box>
                  }
                  secondary={`Conducted by: ${operatorLabel(scan.user)}`}
                />
              );
              return (
                <ListItem
                  key={scan._id || idx}
                  divider={idx < unifiedScans.length - 1}
                  disablePadding={canSeeResults}
                >
                  {canSeeResults ? (
                    <ListItemButton onClick={() => navigate(`/physician/patient/${patientId}/scan/${scan._id}`)}>
                      {text}
                      <ChevronRightIcon color="action" />
                    </ListItemButton>
                  ) : (
                    text
                  )}
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <NotesSection patient={patient} onPatientUpdated={setPatient} />
    </Box>
  );
}

function NotesSection({
  patient,
  onPatientUpdated,
}: {
  patient: Patient;
  onPatientUpdated: (p: Patient) => void;
}) {
  const [notes, setNotes] = useState<PatientSharedNote[]>(
    (patient.shared_notes || [])
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  );
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setError("");
    setSaving(true);
    try {
      const created = await addPatientSharedNote(patient.patient_id, trimmed);
      const updated = [created, ...notes];
      setNotes(updated);
      setText("");
      onPatientUpdated({ ...patient, shared_notes: updated });
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Notes
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder="Type a note..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleAdd}
          disabled={saving || !text.trim()}
          sx={{ minWidth: 110, mt: 0.5 }}
        >
          Add
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notes yet
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 150 }}>Author</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notes.map((n) => (
              <TableRow key={n.note_id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatLocalDateTime(n.created_at)}</TableCell>
                <TableCell>{n.author_name || n.author_email || "—"}</TableCell>
                <TableCell sx={{ whiteSpace: "pre-wrap" }}>{n.text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
